import { queryOptions } from '@tanstack/react-query'
import { api } from '@/api/client'
import type {
  RiotAccountLinkBody,
  RiotAccountMoveBody,
  RiotAccountRanking,
  RiotAccountShow,
  RiotAccountsListQuery,
  RiotAccountsListResponse,
  RiotAccountSummonerName,
  RiotSummoner,
  Server,
} from '@/api/generated/types'

export type RiotAccountListParams = Pick<
  RiotAccountsListQuery,
  'page' | 'page_size' | 'query' | 'server'
>

export const listRiotAccounts = (params: RiotAccountListParams, signal?: AbortSignal) =>
  api.get<RiotAccountsListResponse>('/lol/riot-accounts', { params, signal }).then((r) => r.data)

export const getRiotAccount = (uuid: string, signal?: AbortSignal) =>
  api.get<RiotAccountShow>(`/lol/riot-accounts/${uuid}`, { signal }).then((r) => r.data)

export const getRiotAccountSummonerNames = (uuid: string, signal?: AbortSignal) =>
  api
    .get<RiotAccountSummonerName[]>(`/lol/riot-accounts/${uuid}/summoner-names`, { signal })
    .then((r) => r.data)

export const getRiotAccountRankings = (uuid: string, signal?: AbortSignal) =>
  api
    .get<RiotAccountRanking[]>(`/lol/riot-accounts/${uuid}/rankings`, { signal })
    .then((r) => r.data)

export const deleteRiotAccount = (uuid: string) =>
  api.delete(`/lol/riot-accounts/${uuid}`).then(() => undefined)

export const moveRiotAccount = (uuid: string, body: RiotAccountMoveBody) =>
  api.post<RiotAccountShow>(`/lol/riot-accounts/${uuid}/move`, body).then((r) => r.data)

/** Answers 404 when Riot does not know the id and 409 when the account is already linked. */
export const lookupSummoner = (server: Server, name: string, tagline: string) =>
  api
    .get<RiotSummoner>('/lol/riot/summoner', { params: { server, name, tagline } })
    .then((r) => r.data)

export const linkRiotAccount = (profileUuid: string, body: RiotAccountLinkBody) =>
  api.post<RiotAccountShow>(`/lol/players/${profileUuid}/riot-accounts`, body).then((r) => r.data)

export const riotAccountQueries = {
  all: ['riot-accounts'] as const,
  /** Prefix for every list query, so a link can refresh lists without touching open details. */
  lists: ['riot-accounts', 'list'] as const,
  list: (params: RiotAccountListParams) =>
    queryOptions({
      queryKey: [...riotAccountQueries.lists, params],
      queryFn: ({ signal }) => listRiotAccounts(params, signal),
      placeholderData: (previous) => previous,
    }),
  detail: (uuid: string) =>
    queryOptions({
      queryKey: [...riotAccountQueries.all, 'detail', uuid],
      queryFn: ({ signal }) => getRiotAccount(uuid, signal),
      enabled: Boolean(uuid),
    }),
  summonerNames: (uuid: string) =>
    queryOptions({
      queryKey: [...riotAccountQueries.all, 'summoner-names', uuid],
      queryFn: ({ signal }) => getRiotAccountSummonerNames(uuid, signal),
      // The history card renders its own error inline.
      meta: { silent: true },
    }),
  rankings: (uuid: string) =>
    queryOptions({
      queryKey: [...riotAccountQueries.all, 'rankings', uuid],
      queryFn: ({ signal }) => getRiotAccountRankings(uuid, signal),
      meta: { silent: true },
    }),
}
